import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormContext } from 'react-hook-form';

import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import Checkbox from '@lib/inputs/Checkbox';
import Input from '@lib/inputs/Input';
import { GridStyledWrapper, StyledIdentificationSubheader } from '../voterRegistration.styled';
import { VoterRegistrationFormError } from '../VoterRegistrationFormError';
import { IVoterRegistrationFormValues } from '../voterRegistrationTypes';

function IdentificationSection() {
    const intl = useIntl();
    const {
        watch,
        formState: { errors },
        setValue,
        trigger,
    } = useFormContext<IVoterRegistrationFormValues>();

    const driverLicenseOrPennDotId = watch('identification.driverLicenseOrPennDotId');
    const last4digitsSSN = watch('identification.last4digitsSSN');
    const driverLicenseOrSSNNotAvailable = watch('identification.driverLicenseOrSSNNotAvailable');

    const handleFieldChange = (name: string, value: any) => {
        setValue(name as keyof IVoterRegistrationFormValues, value, {
            shouldValidate: true,
            shouldDirty: true,
        });
        trigger([
            'identification.driverLicenseOrPennDotId',
            'identification.last4digitsSSN',
            'identification.driverLicenseOrSSNNotAvailable',
        ]);
    };

    return (
        <div id="identification-section">
            <FormattedMessage id="voterRegistration.form.identification.header" tagName="h2" />
            <StyledIdentificationSubheader className="required-true">
                <FormattedMessage id="voterRegistration.form.identification.select" />
            </StyledIdentificationSubheader>
            <AlertMessage
                type={'info'}
                content={<FormattedMessage id="voterRegistration.form.identification.alert" />}
                showIcon={true}
                icon={'info-circle'}
            />
            <GridStyledWrapper>
                <div className="fullWidth">
                    <Input
                        label={{
                            text: intl.formatMessage({ id: 'voterRegistration.form.driverLicense' }),
                        }}
                        inputData={{
                            id: 'identification.driverLicenseOrPennDotId',
                            name: 'identification.driverLicenseOrPennDotId',
                            value: driverLicenseOrPennDotId,
                            disabled: driverLicenseOrSSNNotAvailable,
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange('identification.driverLicenseOrPennDotId', val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name="identification.driverLicenseOrPennDotId"
                        hasError={!!errors?.identification?.driverLicenseOrPennDotId}
                        messageId={errors?.identification?.driverLicenseOrPennDotId?.message}
                    />
                </div>
                <div className="fullWidth">
                    <Input
                        label={{
                            text: intl.formatMessage({ id: 'voterRegistration.form.last4digitsSSN' }),
                        }}
                        inputData={{
                            id: 'identification.last4digitsSSN',
                            name: 'identification.last4digitsSSN',
                            value: last4digitsSSN,
                            disabled: driverLicenseOrSSNNotAvailable,
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange('identification.last4digitsSSN', val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name="identification.last4digitsSSN"
                        hasError={!!errors?.identification?.last4digitsSSN}
                        messageId={errors?.identification?.last4digitsSSN?.message}
                    />
                </div>
            </GridStyledWrapper>
            <Checkbox
                label={{
                    text: intl.formatMessage({ id: 'voterRegistration.form.noDriverLicenseOrSSN' }),
                }}
                inputData={{
                    classes: ['margin-top-4'],
                    id: 'identification.driverLicenseOrSSNNotAvailable',
                    name: 'identification.driverLicenseOrSSNNotAvailable',
                    checked: driverLicenseOrSSNNotAvailable,
                    disabled: !!(driverLicenseOrPennDotId || last4digitsSSN),
                }}
                inputActions={{
                    handleFieldChange: val => handleFieldChange('identification.driverLicenseOrSSNNotAvailable', val),
                }}
            />
            <VoterRegistrationFormError
                name="identification.driverLicenseOrSSNNotAvailable"
                hasError={!!errors?.identification?.driverLicenseOrSSNNotAvailable}
                messageId={errors?.identification?.driverLicenseOrSSNNotAvailable?.message}
            />
        </div>
    );
}

export default IdentificationSection;
